import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileText, Send, CheckCircle, XCircle, Clock, Download,
  RefreshCw, Search, Eye, MoreVertical, ChevronLeft,
  ChevronRight, AlertCircle, Ban, QrCode, Zap, Plus,
  CloudDownload, Layers, CalendarIcon, UserPen, ExternalLink,
  CreditCard, Link2, Copy, Scale
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { getGlobalPropertyId, setGlobalProperty } from './propertySync';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const EInvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const pageSizeOptions = [200, 300, 500, 1000, 2000];
  const [stats, setStats] = useState(null);

  // Filters — init from global property selector
  const [selectedProperty, setSelectedProperty] = useState(() => getGlobalPropertyId() || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [checkoutFrom, setCheckoutFrom] = useState(null);
  const [checkoutTo, setCheckoutTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Selection for batch operations
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Modal states
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrInvoice, setQrInvoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [buyerSaving, setBuyerSaving] = useState(false);

  // Buyer edit form
  const [buyerForm, setBuyerForm] = useState({
    guest_name: '', guest_tin: '', guest_id_type: '', guest_id_value: '',
    buyer_sst: '', buyer_email: '', buyer_phone: '',
    buyer_address: '', buyer_city: '', buyer_state: '', buyer_postal_code: '',
    buyer_country: 'MYS'
  });
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);

  // Payment link state
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState(null);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);

  // PDF download state
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);

  // Manual invoice form
  const [manualForm, setManualForm] = useState({
    guest_name: '', description: 'Room Charge', quantity: 1,
    unit_price: 0, tax_amount: 0, total_amount: 0,
    guest_tin: '', guest_id_type: '', guest_id_value: '', invoice_date: ''
  });
  const [consolidateDate, setConsolidateDate] = useState('');
  const [consolidateMode, setConsolidateMode] = useState('daily');
  const [dailyPreview, setDailyPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [consolidatedInvoices, setConsolidatedInvoices] = useState([]);
  const [showConsolidatedSection, setShowConsolidatedSection] = useState(false);

  // Nightly auto-sync status card
  const [nightlyStatus, setNightlyStatus] = useState(null);

  const token = localStorage.getItem('einvoice_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchProperties(); }, []);
  useEffect(() => { fetchInvoices(); }, [page, pageSize, selectedProperty, statusFilter, sourceFilter, typeFilter, checkoutFrom, checkoutTo, searchQuery]);

  // Auto-sync consolidated statuses on initial load (silent, no toast)
  useEffect(() => {
    const autoSync = async () => {
      try {
        await axios.post(`${API}/api/einvoice/gateway/consolidated/sync-status`, {}, { headers: authHeaders });
      } catch {}
    };
    autoSync();
  }, []);

  // Load nightly auto-sync status for the status card
  const fetchNightlyStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/einvoice/gateway/sync/beds24/nightly-status`, { headers: authHeaders });
      setNightlyStatus(data);
    } catch {}
  };
  useEffect(() => { fetchNightlyStatus(); }, []);

  // Bidirectional sync: listen for global property changes
  useEffect(() => {
    const handler = (e) => {
      const prop = e.detail;
      setSelectedProperty(prop ? prop.id : 'all');
      setPage(1);
    };
    window.addEventListener('property-changed', handler);
    return () => window.removeEventListener('property-changed', handler);
  }, []);

  // When page dropdown changes, sync to global
  const handlePropertyChange = (val) => {
    setSelectedProperty(val);
    setPage(1);
    setGlobalProperty(val === 'all' ? null : val, properties);
  };

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API}/api/einvoice/properties`, { headers: authHeaders });
      setProperties(res.data.properties || []);
    } catch (e) { console.error('Error fetching properties:', e); }
  };

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize });
      if (selectedProperty !== 'all') params.append('property_id', selectedProperty);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (typeFilter !== 'all') params.append('invoice_type', typeFilter);
      if (checkoutFrom) {
        const d = checkoutFrom;
        params.append('invoice_date_from', `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
      }
      if (checkoutTo) {
        const d = checkoutTo;
        params.append('invoice_date_to', `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
      }
      if (searchQuery) params.append('search', searchQuery);

      const res = await axios.get(`${API}/api/einvoice/gateway/invoices?${params}`, { headers: authHeaders });
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Error fetching invoices:', e);
      toast.error('Failed to load invoices');
    } finally { setLoading(false); }
  }, [page, pageSize, selectedProperty, statusFilter, sourceFilter, typeFilter, checkoutFrom, checkoutTo, searchQuery]);

  const fetchStats = async (propId) => {
    if (!propId || propId === 'all') { setStats(null); return; }
    try {
      const res = await axios.get(`${API}/api/einvoice/gateway/stats?property_id=${propId}`, { headers: authHeaders });
      setStats(res.data);
    } catch (e) { console.error('Stats error:', e); }
  };

  useEffect(() => { fetchStats(selectedProperty); }, [selectedProperty]);

  // ==================== SYNC FROM BEDS24 ====================
  const handleSyncBeds24 = async ({ full = false } = {}) => {
    const propId = selectedProperty !== 'all' ? selectedProperty : properties[0]?.id;
    if (!propId) { toast.error('Please select a property first'); return; }
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/api/einvoice/gateway/sync/beds24?property_id=${propId}${full ? '&full=true' : ''}`,
        null,
        { headers: authHeaders }
      );
      const { synced, updated, errors, total_bookings, scope } = res.data;
      const scopeLabel = full || scope === 'full' ? 'full history' : `month ${scope}`;
      toast.success(`Beds24 Sync (${scopeLabel}): ${synced} new, ${updated} updated, ${errors} errors (${total_bookings} bookings)`);
      fetchInvoices();
      fetchStats(propId);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Beds24 sync failed';
      if (status === 429) {
        toast.error(detail, { duration: 8000, description: 'Beds24 allows 100 API credits per 5 minutes. Please wait and try again.' });
      } else {
        toast.error(detail);
      }
    } finally { setSyncing(false); }
  };

  // ==================== LHDN SUBMIT ====================
  const handleSubmitToLHDN = async (invoiceIds) => {
    const ids = Array.isArray(invoiceIds) ? invoiceIds : [invoiceIds];
    if (ids.length === 0) { toast.error('No invoices selected'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/einvoice/gateway/invoices/submit-lhdn`,
        { invoice_ids: ids },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      const results = res.data.results || [];
      const success = results.filter(r => r.status === 'submitted').length;
      const failed = results.filter(r => r.error).length;
      if (success > 0) toast.success(`${success} invoice(s) submitted to LHDN`);
      if (failed > 0) toast.error(`${failed} submission(s) failed`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchInvoices();
      fetchStats(selectedProperty);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'LHDN submission failed');
    } finally { setSubmitting(false); }
  };

  // ==================== CHECK STATUS ====================
  const handleCheckStatus = async (invoiceId) => {
    try {
      const res = await axios.post(
        `${API}/api/einvoice/gateway/invoices/${invoiceId}/check-status`,
        null,
        { headers: authHeaders }
      );
      const status = res.data.validation_status || res.data.status;
      toast.success(`Status: ${status?.toUpperCase()}`);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check status');
    }
  };

  // ==================== MANUAL INVOICE ====================
  const handleCreateManual = async () => {
    const propId = selectedProperty !== 'all' ? selectedProperty : properties[0]?.id;
    if (!propId) { toast.error('Please select a property first'); return; }
    if (!manualForm.guest_name.trim()) { toast.error('Guest name is required'); return; }
    if (manualForm.total_amount <= 0) { toast.error('Total amount must be > 0'); return; }
    setSubmitting(true);
    try {
      const payload = { ...manualForm, property_id: propId };
      const res = await axios.post(
        `${API}/api/einvoice/gateway/invoices/manual`,
        payload,
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      toast.success(`Invoice ${res.data.invoice_number} created`);
      setShowManualModal(false);
      setManualForm({ guest_name: '', description: 'Room Charge', quantity: 1, unit_price: 0, tax_amount: 0, total_amount: 0, guest_tin: '', guest_id_type: '', guest_id_value: '', invoice_date: '' });
      fetchInvoices();
      fetchStats(propId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally { setSubmitting(false); }
  };

  // ==================== CONSOLIDATE ====================
  const handleConsolidate = async () => {
    const propId = selectedProperty !== 'all' ? selectedProperty : properties[0]?.id;
    if (!propId || !consolidateDate) { toast.error('Select property and date'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/einvoice/gateway/invoices/consolidate`,
        { property_id: propId, date: consolidateDate, mode: consolidateMode },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      toast.success(`Consolidated ${res.data.invoice_count} invoices into ${res.data.invoice_number}`);
      setShowConsolidateModal(false);
      setDailyPreview(null);
      setShowConsolidatedSection(true);
      fetchConsolidatedInvoices();
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Consolidation failed');
    } finally { setSubmitting(false); }
  };

  const loadDailyPreview = async (date) => {
    const propId = selectedProperty !== 'all' ? selectedProperty : properties[0]?.id;
    if (!propId || !date) return;
    setLoadingPreview(true);
    try {
      const res = await axios.get(
        `${API}/api/einvoice/gateway/invoices/consolidate-preview`,
        { params: { property_id: propId, date }, headers: authHeaders }
      );
      setDailyPreview(res.data);
    } catch (e) {
      setDailyPreview(null);
    } finally { setLoadingPreview(false); }
  };

  // ==================== CONSOLIDATED INVOICES ====================
  const fetchConsolidatedInvoices = async () => {
    try {
      // When "All Properties" is selected, omit property_id so the backend
      // returns consolidated invoices from every property the user can access.
      const params = selectedProperty !== 'all' ? { property_id: selectedProperty } : {};
      const res = await axios.get(
        `${API}/api/einvoice/gateway/consolidated`,
        { params, headers: authHeaders }
      );
      setConsolidatedInvoices(res.data.consolidated_invoices || []);
    } catch (e) { /* ignore */ }
  };

  const handleSubmitConsolidatedToLHDN = async (consolidatedId) => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/einvoice/gateway/consolidated/${consolidatedId}/submit-lhdn`,
        {},
        { headers: authHeaders }
      );
      if (res.data.status === 'submitted') {
        toast.success(`Consolidated invoice submitted to LHDN! UUID: ${res.data.uuid?.slice(0, 12)}...`);
      } else {
        toast.error(`Submission rejected: ${JSON.stringify(res.data.rejected)?.slice(0, 200)}`);
      }
      fetchConsolidatedInvoices();
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'LHDN submission failed');
    } finally { setSubmitting(false); }
  };

  const handleSyncConsolidatedStatus = async () => {
    try {
      const res = await axios.post(
        `${API}/api/einvoice/gateway/consolidated/sync-status`,
        {},
        { headers: authHeaders }
      );
      toast.success(res.data.message);
      fetchInvoices();
      fetchConsolidatedInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    }
  };

  useEffect(() => {
    if (showConsolidatedSection) {
      fetchConsolidatedInvoices();
      // Auto-sync individual invoice statuses from consolidated submissions
      handleSyncConsolidatedStatus();
    }
  }, [showConsolidatedSection, selectedProperty]);

  const handleRecalculate = async () => {
    if (!window.confirm('Recalculate all invoice totals from stored line items? This fixes rounding discrepancies.')) return;
    setRecalculating(true);
    try {
      const res = await axios.post(
        `${API}/api/einvoice/gateway/recalculate-totals`,
        {},
        { headers: authHeaders }
      );
      toast.success(`${res.data.message}. Difference: RM ${res.data.total_difference}`);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recalculation failed');
    } finally { setRecalculating(false); }
  };


  // ==================== EDIT BUYER DETAILS ====================
  const openBuyerModal = (inv) => {
    setEditingInvoiceId(inv.id);
    setBuyerForm({
      guest_name: inv.guest_name || '',
      guest_tin: inv.guest_tin || '',
      guest_id_type: inv.guest_id_type || '',
      guest_id_value: inv.guest_id_value || '',
      buyer_sst: inv.buyer_sst || '',
      buyer_email: inv.buyer_email || '',
      buyer_phone: inv.buyer_phone || '',
      buyer_address: inv.buyer_address || '',
      buyer_city: inv.buyer_city || '',
      buyer_state: inv.buyer_state || '',
      buyer_postal_code: inv.buyer_postal_code || '',
      buyer_country: inv.buyer_country || 'MYS'
    });
    setShowBuyerModal(true);
  };

  const handleSaveBuyer = async () => {
    if (!editingInvoiceId) return;
    setBuyerSaving(true);
    try {
      await axios.put(
        `${API}/api/einvoice/gateway/invoices/${editingInvoiceId}/buyer`,
        buyerForm,
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      toast.success('Buyer details updated');
      setShowBuyerModal(false);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update buyer details');
    } finally { setBuyerSaving(false); }
  };

  // ==================== GENERATE PAYMENT LINK ====================
  const handleGeneratePaymentLink = async (invoiceId, invoiceNumber, total) => {
    setGeneratingPaymentLink(true);
    try {
      const res = await axios.post(
        `${API}/api/einvoice/guest/generate-payment-link/${invoiceId}`,
        null,
        { headers: authHeaders }
      );
      const paymentUrl = `${window.location.origin}/pay/${res.data.payment_token}`;
      setPaymentLinkData({
        url: paymentUrl,
        token: res.data.payment_token,
        expiresAt: res.data.expires_at,
        invoiceNumber: invoiceNumber,
        amount: total,
        reused: res.data.reused
      });
      setShowPaymentLinkModal(true);
      if (res.data.reused) {
        toast.info('Using existing payment link');
      } else {
        toast.success('Payment link generated');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate payment link');
    } finally { setGeneratingPaymentLink(false); }
  };

  const copyPaymentLink = () => {
    if (paymentLinkData?.url) {
      navigator.clipboard.writeText(paymentLinkData.url);
      toast.success('Payment link copied to clipboard');
    }
  };

  // ==================== PDF DOWNLOAD ====================
  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    if (downloadingPdfId) return; // prevent multiple clicks
    setDownloadingPdfId(invoiceId);
    toast.info('Generating PDF…');
    try {
      const res = await fetch(`${API}/api/einvoice/gateway/invoices/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        let msg = 'Failed to download PDF. Please try again.';
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      if (!blob.size || blob.type.includes('json')) {
        throw new Error('Failed to download PDF. Please try again.');
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `e-invoice-${(invoiceNumber || invoiceId).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.message || 'Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdfId(null);
    }
  };

  // Download a single PDF for a consolidated invoice group (one PDF listing
  // every child invoice + consolidated total)
  const handleDownloadConsolidatedPdf = async (consolidatedId, invoiceNumber) => {
    if (downloadingPdfId) return;
    setDownloadingPdfId(`consol-${consolidatedId}`);
    toast.info('Generating consolidated PDF…');
    try {
      const res = await fetch(`${API}/api/einvoice/gateway/consolidated/${consolidatedId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        let msg = 'Failed to download consolidated PDF. Please try again.';
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      if (!blob.size || blob.type.includes('json')) {
        throw new Error('Failed to download consolidated PDF.');
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consolidated-invoice-${(invoiceNumber || consolidatedId).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Consolidated PDF downloaded');
    } catch (e) {
      toast.error(e.message || 'Failed to download consolidated PDF.');
    } finally {
      setDownloadingPdfId(null);
    }
  };

  // ==================== SELECTION ====================
  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) { setSelectedIds(prev => [...prev, invoiceId]); }
    else { setSelectedIds(prev => prev.filter(id => id !== invoiceId)); setSelectAll(false); }
  };
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    // Only allow selection of draft or pending invoices (editable statuses)
    setSelectedIds(checked ? invoices.filter(inv => ['draft', 'pending'].includes(inv.status)).map(inv => inv.id) : []);
  };

  // Helper to check if invoice is editable
  const isInvoiceEditable = (inv) => ['draft', 'pending'].includes(inv.status);

  // ==================== HELPERS ====================
  const getStatusBadge = (status, hasAdjustment) => {
    if (hasAdjustment) {
      return <Badge className="flex items-center gap-1 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"><Scale className="w-3 h-3" />ADJUSTED</Badge>;
    }
    const cfg = {
      draft: { icon: FileText, cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', label: 'DRAFT' },
      pending: { icon: Clock, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'PENDING' },
      submitted: { icon: Send, cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'LHDN SUBMITTED' },
      valid: { icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', label: 'VALIDATED' },
      invalid: { icon: XCircle, cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'INVALID' },
      rejected: { icon: Ban, cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'REJECTED' },
    };
    const c = cfg[status] || cfg.pending;
    const Icon = c.icon;
    return <Badge className={`flex items-center gap-1 text-xs ${c.cls}`}><Icon className="w-3 h-3" />{c.label}</Badge>;
  };

  const getSourceBadge = (source) => {
    if (source === 'beds24') return <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 text-xs">Beds24</Badge>;
    if (source === 'manual') return <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 text-xs">Manual</Badge>;
    if (source === 'excel_upload') return <Badge className="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 text-xs">Excel</Badge>;
    return <Badge variant="outline" className="text-xs">{source || '?'}</Badge>;
  };

  const fmt = (amt) => new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amt || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-5" data-testid="einvoice-list">
      {/* Header + Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#4169E1]" data-testid="invoices-title">e-Invoice Gateway</h1>
          <p className="text-sm text-slate-500 dark:text-gray-400">Sync from PMS, submit to LHDN, track validation</p>
          <p className="text-xs font-medium text-[#4169E1] mt-1" data-testid="viewing-property-label">
            Viewing: {selectedProperty === 'all' ? 'All Properties' : (properties.find(p => p.id === selectedProperty)?.property_name || selectedProperty)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => handleSyncBeds24()} disabled={syncing} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sync-beds24-btn">
            {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-2" />}
            Sync from Beds24 (This Month)
          </Button>
          <Button onClick={() => handleSyncBeds24({ full: true })} disabled={syncing} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="sync-beds24-full-btn" title="Slower — pulls the entire history from your extraction start date">
            <CloudDownload className="w-4 h-4 mr-2" /> Sync All
          </Button>
          <Button onClick={() => setShowManualModal(true)} className="bg-slate-600 hover:bg-slate-700 text-white" data-testid="manual-invoice-btn">
            <Plus className="w-4 h-4 mr-2" /> Manual Entry
          </Button>
          <Button onClick={() => setShowConsolidateModal(true)} className="bg-slate-600 hover:bg-slate-700 text-white" data-testid="consolidate-btn">
            <Layers className="w-4 h-4 mr-2" /> Consolidate
          </Button>
          <Button onClick={handleRecalculate} disabled={recalculating} variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20" data-testid="recalculate-btn">
            {recalculating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recalculate
          </Button>
          <Button onClick={fetchInvoices} variant="ghost" size="icon" className="text-slate-600 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-white/10" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Nightly Auto-Sync Status Card */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/60 dark:bg-indigo-500/5 px-4 py-2.5"
        data-testid="nightly-status-card"
      >
        <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
          <Clock className="w-4 h-4" />
          <span className="text-xs font-semibold uppercase tracking-wide">Nightly Auto-Sync</span>
        </div>
        {nightlyStatus?.last_run ? (
          <>
            <span className="text-sm text-slate-600 dark:text-gray-300" data-testid="nightly-last-run">
              Last run: <span className="font-medium">{nightlyStatus.last_run.ran_at_myt} MYT</span>
              {nightlyStatus.last_run.scope ? ` · month ${nightlyStatus.last_run.scope}` : ''}
            </span>
            <div className="flex items-center gap-1.5" data-testid="nightly-counts">
              <Badge variant="outline" className="border-emerald-300 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 text-xs">
                {nightlyStatus.last_run.totals?.synced ?? 0} new
              </Badge>
              <Badge variant="outline" className="border-blue-300 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 text-xs">
                {nightlyStatus.last_run.totals?.updated ?? 0} updated
              </Badge>
              <Badge variant="outline" className={`text-xs ${(nightlyStatus.last_run.totals?.errors ?? 0) > 0 ? 'border-red-300 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10' : 'border-slate-300 text-slate-600 dark:text-gray-400'}`}>
                {nightlyStatus.last_run.totals?.errors ?? 0} errors
              </Badge>
              <span className="text-xs text-slate-400">· {nightlyStatus.last_run.properties_count ?? 0} properties</span>
            </div>
          </>
        ) : (
          <span className="text-sm text-slate-500 dark:text-gray-400" data-testid="nightly-no-run">
            Scheduled for {String(nightlyStatus?.run_hour_myt ?? 3).padStart(2, '0')}:00 MYT daily — no run recorded yet.
          </span>
        )}
        <Button
          onClick={fetchNightlyStatus}
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/10"
          data-testid="nightly-status-refresh"
          title="Refresh nightly status"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-cards">
          {[
            { label: 'Total', value: stats.total, color: 'text-slate-700 dark:text-gray-200' },
            { label: 'Pending', value: stats.pending, color: 'text-amber-600' },
            { label: 'Submitted', value: stats.submitted, color: 'text-blue-600' },
            { label: 'Valid', value: stats.valid, color: 'text-emerald-600' },
            { label: 'Invalid', value: stats.invalid, color: 'text-red-600' },
            { label: 'Total Revenue', value: fmt(stats.total_amount), color: 'text-[#4169E1]' },
          ].map(s => (
            <Card key={s.label} className="bg-white/60 dark:bg-white/5 backdrop-blur">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-slate-500 dark:text-gray-400 uppercase tracking-wide">{s.label}</p>
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Batch Actions Bar */}
      {selectedIds.length > 0 && (
        <Card className="border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="py-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{selectedIds.length} selected</span>
            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300" data-testid="selected-total-amount">
              Total: RM {invoices.filter(inv => selectedIds.includes(inv.id)).reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <Button onClick={() => handleSubmitToLHDN(selectedIds)} disabled={submitting} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="batch-submit-btn">
              {submitting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Submit to LHDN
            </Button>
            <Button onClick={() => { setSelectedIds([]); setSelectAll(false); }} size="sm" variant="ghost">Clear</Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-slate-500 dark:text-gray-400">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Invoice #, Guest, Beds24 ID..." value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }} className="pl-9 w-[220px]" data-testid="search-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 dark:text-gray-400">Property</Label>
              <Select value={selectedProperty} onValueChange={handlePropertyChange}>
                <SelectTrigger className="w-[180px]" data-testid="property-filter"><SelectValue placeholder="All Properties" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_name || p.name || p.id.slice(0, 8)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 dark:text-gray-400">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="valid">Validated</SelectItem>
                  <SelectItem value="invalid">Invalid</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="adjusted">Adjusted (CN/DN)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 dark:text-gray-400">Source</Label>
              <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[130px]" data-testid="source-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="beds24">Beds24</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="excel_upload">Excel Upload</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 dark:text-gray-400">Type</Label>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]" data-testid="type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="consolidated">Consolidated</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 dark:text-gray-400">Invoice Date From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[150px] justify-start text-left font-normal" data-testid="checkout-from-filter">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {checkoutFrom ? checkoutFrom.toLocaleDateString() : <span className="text-muted-foreground">From</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={checkoutFrom} onSelect={(d) => { setCheckoutFrom(d); setPage(1); }} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs text-slate-500 dark:text-gray-400">Invoice Date To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[150px] justify-start text-left font-normal" data-testid="checkout-to-filter">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {checkoutTo ? checkoutTo.toLocaleDateString() : <span className="text-muted-foreground">To</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={checkoutTo} onSelect={(d) => { setCheckoutTo(d); setPage(1); }} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            {(checkoutFrom || checkoutTo) && (
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={() => { setCheckoutFrom(null); setCheckoutTo(null); }} className="text-xs text-red-500 hover:text-red-700" data-testid="clear-dates-btn">
                  <XCircle className="w-3 h-3 mr-1" /> Clear dates
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><RefreshCw className="w-6 h-6 animate-spin text-[#4169E1]" /></div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-16 text-slate-400 dark:text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No invoices found</p>
              <p className="text-sm mt-1">Sync from Beds24 or create a manual invoice to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} data-testid="select-all-checkbox" />
                  </TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead className="whitespace-nowrap">Booking ID</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Guest</TableHead>
                  {selectedProperty === 'all' && <TableHead>Property</TableHead>}
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>LHDN UUID</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <TableCell>
                      {isInvoiceEditable(inv) && (
                        <Checkbox checked={selectedIds.includes(inv.id)} onCheckedChange={(c) => handleSelectInvoice(inv.id, c)} data-testid={`checkbox-${inv.id}`} />
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600 dark:text-gray-300" data-testid={`booking-id-${inv.id}`}>
                      {inv.pms_booking_id || inv.booking_ref || <span className="text-slate-300 dark:text-gray-600">-</span>}
                    </TableCell>
                    <TableCell>{getSourceBadge(inv.source)}</TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{inv.guest_name || '-'}</p>
                      {inv.consolidated_invoice_id && (
                        <Badge className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 mt-0.5" title={`Grouped into ${inv.consolidated_lhdn_number || 'consolidated invoice'}`} data-testid={`consolidated-badge-${inv.id}`}>
                          <Layers className="w-3 h-3 mr-1" />
                          Consolidated {inv.consolidated_lhdn_number ? `· ${inv.consolidated_lhdn_number}` : ''}
                        </Badge>
                      )}
                      {!inv.consolidated_invoice_id && inv.guest_tin === 'EI00000000010' && <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 mt-0.5">B2C · Consolidated eligible</Badge>}
                      {inv.check_in && <p className="text-xs text-slate-400">{inv.check_in} - {inv.check_out}</p>}
                    </TableCell>
                    {selectedProperty === 'all' && (
                      <TableCell className="text-sm text-slate-600 dark:text-gray-300">
                        {properties.find(p => p.id === inv.property_id)?.property_name || '-'}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-medium">{fmt(inv.total_amount)}</TableCell>
                    <TableCell>{getStatusBadge(inv.status, inv.has_adjustment)}</TableCell>
                    <TableCell className="font-mono text-xs max-w-[120px] truncate">
                      {inv.lhdn_uuid ? (
                        <span className="flex items-center gap-1">
                          {inv.lhdn_uuid.slice(0, 12)}...
                          {inv.qr_code_url && <QrCode className="w-3 h-3 text-emerald-500 shrink-0" />}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 dark:text-gray-400">{fmtDate(inv.check_in)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}
                          disabled={downloadingPdfId === inv.id}
                          data-testid={`pdf-btn-${inv.id}`}
                        >
                          {downloadingPdfId === inv.id
                            ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> PDF</>
                            : <><Download className="w-3 h-3 mr-1" /> PDF</>}
                        </Button>
                        {(inv.status === 'draft' || inv.status === 'pending') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => openBuyerModal(inv)}
                            data-testid={`buyer-btn-${inv.id}`}
                          >
                            <UserPen className="w-3 h-3 mr-1" /> Buyer
                          </Button>
                        )}
                        {(inv.status === 'draft' || inv.status === 'pending') && (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => handleSubmitToLHDN(inv.id)}
                            disabled={submitting}
                            data-testid={`submit-btn-${inv.id}`}
                          >
                            <Send className="w-3 h-3 mr-1" /> Submit
                          </Button>
                        )}
                        {(inv.status === 'submitted' || inv.status === 'valid') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handleCheckStatus(inv.id)}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Check
                          </Button>
                        )}
                        {(inv.status === 'submitted' || inv.status === 'valid' || inv.status === 'validated') && inv.lhdn_uuid && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs border-purple-300 text-purple-700 hover:bg-purple-50 dark:text-purple-400 dark:border-purple-700 dark:hover:bg-purple-900/20"
                                  onClick={() => navigate(`/einvoice/portal/adjustment/create?invoice_id=${inv.id}`)}
                                  data-testid={`adjustment-btn-${inv.id}`}
                                >
                                  <Scale className="w-3 h-3 mr-1" /> Adjust
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Adjustments are only allowed after submission to LHDN</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {inv.lhdn_uuid && inv.qr_code_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-900/20"
                            onClick={() => { setQrInvoice(inv); setShowQrModal(true); }}
                            data-testid={`qr-btn-${inv.id}`}
                          >
                            <QrCode className="w-3 h-3 mr-1" /> QR
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-${inv.id}`}><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/api/einvoice/gateway/invoices/${inv.id}`, { headers: authHeaders });
                                setSelectedInvoice(res.data);
                              } catch { setSelectedInvoice(inv); }
                              setShowDetailModal(true);
                            }}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {(inv.status === 'submitted' || inv.status === 'valid' || inv.status === 'validated') && inv.lhdn_uuid && (
                              <DropdownMenuItem onClick={() => navigate(`/einvoice/adjustment/create?invoice_id=${inv.id}`)}>
                                <Scale className="w-4 h-4 mr-2" /> e-Invoice Adjustment
                              </DropdownMenuItem>
                            )}
                            {(inv.status === 'draft' || inv.status === 'pending') && (
                              <DropdownMenuItem disabled className="text-slate-400 dark:text-gray-500 text-xs cursor-not-allowed">
                                <Scale className="w-4 h-4 mr-2" /> 
                                <span>Adjustment unavailable — Edit in PMS</span>
                              </DropdownMenuItem>
                            )}
                            {inv.payment_status !== 'paid' && (
                              <DropdownMenuItem 
                                onClick={() => handleGeneratePaymentLink(inv.id, inv.invoice_number, inv.total)}
                                disabled={generatingPaymentLink}
                              >
                                <CreditCard className="w-4 h-4 mr-2" /> Generate Payment Link
                              </DropdownMenuItem>
                            )}
                            {(inv.lhdn_uuid && inv.qr_code_url) && (
                              <DropdownMenuItem onClick={() => window.open(inv.qr_code_url, '_blank')}>
                                <QrCode className="w-4 h-4 mr-2" /> Verify on MyInvois
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-zinc-800 flex-wrap gap-2" data-testid="invoices-pagination">
          <div className="flex items-center gap-3">
            <p className="text-xs text-slate-500 dark:text-gray-400">
              {total > 0 ? `Showing ${((page - 1) * pageSize) + 1}–${Math.min(page * pageSize, total)} of ${total} invoices` : 'No records'}
            </p>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">Per page:</span>
              <select
                data-testid="invoices-page-size-select"
                className="h-7 px-2 text-xs border border-slate-200 dark:border-zinc-700 rounded bg-white dark:bg-zinc-900 text-slate-700 dark:text-gray-300"
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)} data-testid="invoices-page-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {totalPages > 0 && [...Array(Math.min(totalPages, 5))].map((_, i) => {
              const p = page <= 3 ? i + 1 : page - 2 + i;
              if (p < 1 || p > totalPages) return null;
              return (
                <Button key={p} variant={p === page ? "default" : "outline"} size="sm"
                  className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-blue-600 text-white' : ''}`}
                  onClick={() => setPage(p)} data-testid={`invoices-page-${p}`}>
                  {p}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)} data-testid="invoices-page-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Consolidated Invoices Section */}
      <Card className="border border-slate-200 dark:border-zinc-800 shadow-sm" data-testid="consolidated-invoices-section">
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors"
          onClick={() => { setShowConsolidatedSection(v => !v); if (!showConsolidatedSection) fetchConsolidatedInvoices(); }}
          data-testid="consolidated-toggle"
        >
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-slate-800 dark:text-white">Consolidated Invoices</h2>
            {consolidatedInvoices.length > 0 && (
              <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs">{consolidatedInvoices.length}</Badge>
            )}
            {showConsolidatedSection && consolidatedInvoices.some(ci => ci.lhdn_uuid) && (
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[10px] px-2 border-blue-300 text-blue-600 hover:bg-blue-50 ml-2"
                onClick={(e) => { e.stopPropagation(); handleSyncConsolidatedStatus(); }}
                data-testid="sync-consolidated-status-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Sync Invoice Status
              </Button>
            )}
          </div>
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${showConsolidatedSection ? 'rotate-90' : ''}`} />
        </div>
        {showConsolidatedSection && (
          <CardContent className="p-0">
            {consolidatedInvoices.length === 0 ? (
              <p className="text-sm text-slate-400 px-4 py-6 text-center">No consolidated invoices yet. Use the Consolidate button to group B2C invoices.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Invoice #</TableHead>
                    <TableHead className="text-xs">Mode</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Invoices</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">LHDN UUID</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consolidatedInvoices.map(ci => (
                    <TableRow key={ci.id} data-testid={`consolidated-row-${ci.invoice_number}`}>
                      <TableCell className="font-mono text-xs font-medium">{ci.invoice_number}</TableCell>
                      <TableCell>
                        <Badge className={ci.consolidation_mode === 'daily' ? 'bg-blue-100 text-blue-700 text-[10px]' : 'bg-purple-100 text-purple-700 text-[10px]'}>
                          {ci.consolidation_mode === 'daily' ? 'Daily' : 'Monthly'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 dark:text-gray-400">
                        <div>{ci.consolidation_date}</div>
                        {ci.lhdn_submission_deadline_label && !ci.lhdn_uuid && (
                          <div
                            className={`text-[10px] mt-0.5 ${
                              ci.lhdn_submission_deadline_passed
                                ? 'text-red-600 font-semibold'
                                : 'text-slate-400'
                            }`}
                            title="LHDN consolidated e-invoice submission deadline"
                            data-testid={`lhdn-deadline-${ci.invoice_number}`}
                          >
                            {ci.lhdn_submission_deadline_passed
                              ? `Deadline passed (${ci.lhdn_submission_deadline_label})`
                              : `LHDN deadline: ${ci.lhdn_submission_deadline_label}`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{ci.invoice_count}</TableCell>
                      <TableCell className="text-xs text-right font-medium">{fmt(ci.total_amount)}</TableCell>
                      <TableCell>
                        {ci.status === 'pending' && <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Clock className="w-3 h-3 mr-1" />PENDING</Badge>}
                        {ci.status === 'grouped' && <Badge className="bg-slate-100 text-slate-700 text-[10px]"><Layers className="w-3 h-3 mr-1" />GROUPED</Badge>}
                        {ci.status === 'submitted' && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Send className="w-3 h-3 mr-1" />SUBMITTED</Badge>}
                        {ci.status === 'valid' && <Badge className="bg-green-100 text-green-700 text-[10px]"><CheckCircle className="w-3 h-3 mr-1" />VALID</Badge>}
                        {ci.status === 'rejected' && <Badge className="bg-red-100 text-red-700 text-[10px]"><XCircle className="w-3 h-3 mr-1" />REJECTED</Badge>}
                        {ci.status === 'error' && <Badge className="bg-red-100 text-red-700 text-[10px]"><AlertCircle className="w-3 h-3 mr-1" />ERROR</Badge>}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-slate-400">{ci.lhdn_uuid ? ci.lhdn_uuid.slice(0, 12) + '...' : '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs px-2"
                            onClick={() => handleDownloadConsolidatedPdf(ci.id, ci.invoice_number)}
                            disabled={downloadingPdfId === `consol-${ci.id}`}
                            data-testid={`download-consolidated-pdf-${ci.invoice_number}`}
                          >
                            {downloadingPdfId === `consol-${ci.id}`
                              ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> PDF</>
                              : <><Download className="w-3 h-3 mr-1" /> PDF</>}
                          </Button>
                          {ci.status === 'pending' && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-3 disabled:opacity-50"
                              onClick={() => handleSubmitConsolidatedToLHDN(ci.id)}
                              disabled={submitting || ci.lhdn_submission_deadline_passed}
                              title={ci.lhdn_submission_deadline_passed
                                ? `LHDN deadline (${ci.lhdn_submission_deadline_label}) has passed — submission is no longer permitted.`
                                : 'Submit consolidated invoice to LHDN'}
                              data-testid={`submit-consolidated-${ci.invoice_number}`}
                            >
                              <Send className="w-3 h-3 mr-1" /> Submit
                            </Button>
                          )}
                          {(ci.status === 'rejected' || ci.status === 'error') && !ci.lhdn_uuid && (
                            <Button
                              size="sm"
                              className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-7 px-3 disabled:opacity-50"
                              onClick={() => handleSubmitConsolidatedToLHDN(ci.id)}
                              disabled={submitting || ci.lhdn_submission_deadline_passed}
                              title={ci.lhdn_submission_deadline_passed
                                ? `LHDN deadline (${ci.lhdn_submission_deadline_label}) has passed — resubmission is no longer permitted.`
                                : 'Retry submission to LHDN (previous attempt failed)'}
                              data-testid={`resubmit-consolidated-${ci.invoice_number}`}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" /> Resubmit
                            </Button>
                          )}
                        </div>
                        {ci.lhdn_error && (
                          <p className="text-[10px] text-red-500 mt-1 max-w-[200px] truncate" title={ci.lhdn_error}>{ci.lhdn_error}</p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>

      {/* Invoice Detail Modal — Full LHDN-compliant view */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Invoice Details <span className="font-mono text-sm text-slate-400">{selectedInvoice?.invoice_number}</span></DialogTitle>
            <DialogDescription>LHDN e-Invoice submission data</DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-5 text-sm">
              {/* Row 1: Invoice Info + Status */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Invoice #</Label><p className="font-mono font-medium">{selectedInvoice.invoice_number}</p></div>
                <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Status</Label><div className="mt-0.5">{getStatusBadge(selectedInvoice.status)}</div></div>
                <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Source</Label><div className="mt-0.5">{getSourceBadge(selectedInvoice.source)}</div></div>
                <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Currency</Label><p>{selectedInvoice.currency || 'MYR'}</p></div>
              </div>

              {/* Seller Information */}
              {selectedInvoice.seller && Object.values(selectedInvoice.seller).some(v => v) && (
                <div className="border rounded-lg p-3 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">Seller (Hotel Profile)</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><Label className="text-[11px] text-slate-400">Name</Label><p>{selectedInvoice.seller.name || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">TIN</Label><p className="font-mono">{selectedInvoice.seller.tin || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">BRN</Label><p className="font-mono">{selectedInvoice.seller.brn || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">SST No.</Label><p className="font-mono">{selectedInvoice.seller.sst_number || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Tourism Tax No.</Label><p className="font-mono">{selectedInvoice.seller.tourism_tax_number || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">MSIC Code</Label><p className="font-mono">{selectedInvoice.seller.msic_code || '-'}</p></div>
                  </div>
                  <div className="mt-2">
                    <Label className="text-[11px] text-slate-400">Address</Label>
                    <p className="text-xs">{[selectedInvoice.seller.address_line1, selectedInvoice.seller.city, selectedInvoice.seller.state, selectedInvoice.seller.postal_code].filter(Boolean).join(', ') || '-'}</p>
                  </div>
                </div>
              )}

              {/* Buyer Information */}
              <div className="border rounded-lg p-3 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">Buyer (Guest)</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div><Label className="text-[11px] text-slate-400">Name</Label><p className="font-medium">{selectedInvoice.guest_name || '-'}</p></div>
                  <div><Label className="text-[11px] text-slate-400">TIN</Label>
                    <p className={`font-mono ${selectedInvoice.guest_tin === 'EI00000000010' ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                      {selectedInvoice.guest_tin || '-'}
                      {selectedInvoice.guest_tin === 'EI00000000010' && <span className="text-[10px] ml-1">(Default)</span>}
                    </p>
                  </div>
                  <div><Label className="text-[11px] text-slate-400">Buyer Type</Label><p className="capitalize">{selectedInvoice.buyer_type || 'individual'}</p></div>
                  <div><Label className="text-[11px] text-slate-400">ID Type</Label><p>{selectedInvoice.guest_id_type || '-'}</p></div>
                  <div><Label className="text-[11px] text-slate-400">ID Number</Label><p className="font-mono">{selectedInvoice.guest_id_value || '-'}</p></div>
                  <div><Label className="text-[11px] text-slate-400">Country</Label><p>{selectedInvoice.buyer_country || 'MYS'}</p></div>
                </div>
                {selectedInvoice.consolidated && (
                  <div className="mt-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1">
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 font-medium">Consolidated eligible — Will be included in monthly consolidated e-Invoice</p>
                  </div>
                )}
              </div>

              {/* Booking Info (for Beds24-synced invoices) */}
              {(selectedInvoice.check_in || selectedInvoice.room_type || selectedInvoice.booking_ref || selectedInvoice.pms_booking_id) && (
                <div className="border rounded-lg p-3 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">Booking Information</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><Label className="text-[11px] text-slate-400">Beds24 Booking ID</Label><p className="font-mono" data-testid="detail-beds24-booking-id">{selectedInvoice.pms_booking_id || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Check-in</Label><p>{fmtDate(selectedInvoice.check_in)}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Check-out</Label><p>{fmtDate(selectedInvoice.check_out)}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Nights</Label><p>{selectedInvoice.num_nights || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Room Type</Label><p>{selectedInvoice.room_type || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Booking Ref</Label><p className="font-mono">{selectedInvoice.booking_ref || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Invoice Date</Label><p>{fmtDate(selectedInvoice.invoice_date)}</p></div>
                  </div>
                </div>
              )}

              {/* Line Items */}
              {selectedInvoice.line_items?.length > 0 && (
                <div className="border rounded-lg dark:border-gray-700 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 dark:bg-zinc-900 border-b dark:border-gray-700">
                    <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide">Line Items</h4>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b dark:border-gray-700 bg-slate-50/50 dark:bg-zinc-900/50">
                        <th className="text-left px-3 py-1.5 font-medium text-slate-500">Description</th>
                        <th className="text-center px-2 py-1.5 font-medium text-slate-500 w-12">Qty</th>
                        <th className="text-right px-2 py-1.5 font-medium text-slate-500 w-20">Unit Price</th>
                        <th className="text-right px-2 py-1.5 font-medium text-slate-500 w-16">Tax %</th>
                        <th className="text-right px-2 py-1.5 font-medium text-slate-500 w-16">Tax</th>
                        <th className="text-right px-3 py-1.5 font-medium text-slate-500 w-20">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.line_items.map((li, i) => (
                        <tr key={i} className="border-b dark:border-gray-800">
                          <td className="px-3 py-1.5">{li.description}</td>
                          <td className="text-center px-2 py-1.5">{li.quantity}</td>
                          <td className="text-right px-2 py-1.5 font-mono">{(li.unit_price || 0).toFixed(2)}</td>
                          <td className="text-right px-2 py-1.5">{li.tax_rate != null ? `${li.tax_rate}%` : '-'}</td>
                          <td className="text-right px-2 py-1.5 font-mono">{(li.tax_amount || 0).toFixed(2)}</td>
                          <td className="text-right px-3 py-1.5 font-mono font-medium">{(li.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Financial Summary */}
              <div className="flex justify-end">
                <div className="w-56 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">{fmt(selectedInvoice.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="font-mono">{fmt(selectedInvoice.tax_amount)}</span></div>
                  <div className="flex justify-between border-t pt-1 dark:border-gray-700"><span className="font-semibold">Total</span><span className="font-bold font-mono text-[#4169E1]">{fmt(selectedInvoice.total_amount)}</span></div>
                </div>
              </div>

              {/* LHDN Submission Data */}
              {(selectedInvoice.lhdn_uuid || selectedInvoice.submission_uid || selectedInvoice.validation_status) && (
                <div className="border rounded-lg p-3 dark:border-gray-700 bg-slate-50/50 dark:bg-zinc-900/50">
                  <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">LHDN Submission Data</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px] text-slate-400">Submission UID</Label><p className="font-mono text-xs break-all">{selectedInvoice.submission_uid || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">UUID</Label><p className="font-mono text-xs break-all">{selectedInvoice.lhdn_uuid || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Validation Status</Label>
                      <p className={`font-medium ${selectedInvoice.validation_status === 'valid' ? 'text-emerald-600' : selectedInvoice.validation_status === 'invalid' ? 'text-red-600' : 'text-amber-600'}`}>
                        {(selectedInvoice.validation_status || '-').toUpperCase()}
                      </p>
                    </div>
                    <div><Label className="text-[11px] text-slate-400">LHDN Timestamp</Label><p className="text-xs">{selectedInvoice.validation_timestamp ? new Date(selectedInvoice.validation_timestamp).toLocaleString('en-MY') : '-'}</p></div>
                  </div>
                  {selectedInvoice.qr_code_url && (
                    <div className="mt-3 pt-3 border-t dark:border-gray-700">
                      <div className="flex items-start gap-4">
                        <div className="bg-white p-2 rounded-lg shadow-sm border" data-testid="detail-qr-code">
                          <QRCodeSVG value={selectedInvoice.qr_code_url} size={120} level="M" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Label className="text-[11px] text-slate-400">MyInvois Verification</Label>
                          <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Scan the QR code or click the link below to verify this e-Invoice on the LHDN MyInvois portal.</p>
                          <a href={selectedInvoice.qr_code_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs break-all mt-1 inline-flex items-center gap-1" data-testid="detail-verification-link">
                            <ExternalLink className="w-3 h-3 shrink-0" /> {selectedInvoice.qr_code_url}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Invoice Modal */}
      <Dialog open={showManualModal} onOpenChange={setShowManualModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Manual Invoice</DialogTitle>
            <DialogDescription>For walk-in guests or manual entries</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Guest Name *</Label>
              <Input value={manualForm.guest_name} onChange={e => setManualForm(f => ({ ...f, guest_name: e.target.value }))} placeholder="Guest full name" data-testid="manual-guest-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Description</Label>
                <Input value={manualForm.description} onChange={e => setManualForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div>
                <Label>Invoice Date</Label>
                <Input type="date" value={manualForm.invoice_date} onChange={e => setManualForm(f => ({ ...f, invoice_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Qty</Label>
                <Input type="number" min="1" value={manualForm.quantity} onChange={e => setManualForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 1 }))} />
              </div>
              <div>
                <Label>Unit Price (MYR)</Label>
                <Input type="number" step="0.01" value={manualForm.unit_price} onChange={e => setManualForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} data-testid="manual-unit-price" />
              </div>
              <div>
                <Label>Tax (MYR)</Label>
                <Input type="number" step="0.01" value={manualForm.tax_amount} onChange={e => setManualForm(f => ({ ...f, tax_amount: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>
            <div>
              <Label>Total Amount (MYR) *</Label>
              <Input type="number" step="0.01" value={manualForm.total_amount} onChange={e => setManualForm(f => ({ ...f, total_amount: parseFloat(e.target.value) || 0 }))} data-testid="manual-total" />
            </div>
            <details className="border rounded-lg p-3 dark:border-gray-700">
              <summary className="text-sm font-medium cursor-pointer text-slate-600 dark:text-gray-300">LHDN Tax Details (Optional)</summary>
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Guest TIN</Label><Input value={manualForm.guest_tin} onChange={e => setManualForm(f => ({ ...f, guest_tin: e.target.value }))} placeholder="e.g. C12345678" /></div>
                  <div><Label className="text-xs">ID Type</Label>
                    <Select value={manualForm.guest_id_type} onValueChange={v => setManualForm(f => ({ ...f, guest_id_type: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NRIC">NRIC</SelectItem>
                        <SelectItem value="PASSPORT">Passport</SelectItem>
                        <SelectItem value="BRN">BRN</SelectItem>
                        <SelectItem value="ARMY">Army ID</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs">ID Value</Label><Input value={manualForm.guest_id_value} onChange={e => setManualForm(f => ({ ...f, guest_id_value: e.target.value }))} placeholder="ID number" /></div>
              </div>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualModal(false)}>Cancel</Button>
            <Button onClick={handleCreateManual} disabled={submitting} data-testid="create-manual-btn">
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consolidate Modal — Monthly / Daily */}
      <Dialog open={showConsolidateModal} onOpenChange={(open) => { setShowConsolidateModal(open); if (!open) { setDailyPreview(null); setConsolidateDate(''); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consolidated e-Invoice</DialogTitle>
            <DialogDescription>Group B2C invoices (Buyer TIN = EI00000000010) for LHDN submission</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Mode Selector */}
            <div>
              <Label className="mb-2 block text-sm font-medium">Consolidation Type</Label>
              <div className="flex gap-2">
                <Button
                  variant={consolidateMode === 'daily' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setConsolidateMode('daily'); setDailyPreview(null); setConsolidateDate(''); }}
                  data-testid="consolidate-mode-daily"
                  className={consolidateMode === 'daily' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                  <CalendarIcon className="w-4 h-4 mr-1.5" /> Daily
                </Button>
                <Button
                  variant={consolidateMode === 'monthly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setConsolidateMode('monthly'); setDailyPreview(null); setConsolidateDate(''); }}
                  data-testid="consolidate-mode-monthly"
                  className={consolidateMode === 'monthly' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                  <Layers className="w-4 h-4 mr-1.5" /> Monthly
                </Button>
              </div>
            </div>

            {/* Date Picker — changes based on mode */}
            <div>
              <Label>{consolidateMode === 'daily' ? 'Select Date' : 'Select Month'}</Label>
              <Input
                type={consolidateMode === 'daily' ? 'date' : 'month'}
                value={consolidateDate}
                onChange={e => {
                  setConsolidateDate(e.target.value);
                  if (consolidateMode === 'daily' && e.target.value) {
                    loadDailyPreview(e.target.value);
                  }
                }}
                data-testid="consolidate-date"
              />
            </div>

            {/* Daily Preview — shows invoice count and total for selected date */}
            {consolidateMode === 'daily' && consolidateDate && (
              <div className="border rounded-lg overflow-hidden">
                {loadingPreview ? (
                  <div className="flex items-center justify-center py-6 text-sm text-slate-500">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading...
                  </div>
                ) : dailyPreview ? (
                  <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{consolidateDate}</p>
                        <p className="text-xs text-slate-500">{dailyPreview.total_invoices} eligible invoice(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900 dark:text-white">RM {dailyPreview.grand_total?.toLocaleString('en-MY', {minimumFractionDigits: 2})}</p>
                      </div>
                    </div>
                    {dailyPreview.total_invoices > 0 && dailyPreview.daily_breakdown?.[0]?.invoices && (
                      <div className="mt-2 max-h-40 overflow-y-auto border-t pt-2">
                        {dailyPreview.daily_breakdown[0].invoices.map((inv, i) => (
                          <div key={i} className="flex justify-between text-xs py-0.5 text-slate-600 dark:text-slate-400">
                            <span>{inv.invoice_number} — {inv.guest_name}</span>
                            <span className="font-mono">{inv.total_amount?.toLocaleString('en-MY', {minimumFractionDigits: 2})}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {dailyPreview.total_invoices === 0 && (
                      <p className="mt-1 text-xs text-amber-600">No eligible B2C invoices for this date</p>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Info Box */}
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {consolidateMode === 'daily' ? (
                  <>
                    <strong>Daily Consolidation:</strong> Creates one consolidated e-Invoice for the selected date only.
                    All B2C invoices (no buyer identification) for that specific date are grouped — ideal for <strong>daily audit and reconciliation with Beds24</strong>.
                  </>
                ) : (
                  <>
                    <strong>LHDN Deadline:</strong> Consolidated B2C e-Invoices must be submitted within <strong>7 calendar days after the end of the month</strong> (by the <strong>7th of the following month</strong>).
                    {consolidateDate && (
                      <span className="block mt-1 font-mono">
                        {consolidateDate} transactions &rarr; Deadline: {(() => {
                          try {
                            const [y, m] = consolidateDate.split('-').map(Number);
                            const nm = m < 12 ? m + 1 : 1;
                            const ny = m < 12 ? y : y + 1;
                            return `${ny}-${String(nm).padStart(2, '0')}-07`;
                          } catch { return ''; }
                        })()}
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowConsolidateModal(false); setDailyPreview(null); }}>Cancel</Button>
            <Button
              onClick={handleConsolidate}
              disabled={submitting || !consolidateDate || (consolidateMode === 'daily' && dailyPreview?.total_invoices === 0)}
              data-testid="run-consolidate-btn"
            >
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}
              Consolidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Buyer Details Modal */}
      <Dialog open={showBuyerModal} onOpenChange={setShowBuyerModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="buyer-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPen className="w-5 h-5" /> Edit Buyer Details
            </DialogTitle>
            <DialogDescription>Update buyer information before submitting to LHDN MyInvois. Once submitted, the invoice cannot be edited.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Buyer Name *</Label>
              <Input
                value={buyerForm.guest_name}
                onChange={e => setBuyerForm(f => ({ ...f, guest_name: e.target.value }))}
                placeholder="Full name"
                data-testid="buyer-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tax Identification Number (TIN) *</Label>
                <Input
                  value={buyerForm.guest_tin}
                  onChange={e => setBuyerForm(f => ({ ...f, guest_tin: e.target.value }))}
                  placeholder="e.g. C12345678"
                  data-testid="buyer-tin-input"
                />
                <p className="text-xs text-slate-400 mt-1">Use "EI00000000010" for foreign buyers</p>
              </div>
              <div>
                <Label>ID Type *</Label>
                <Select value={buyerForm.guest_id_type} onValueChange={v => setBuyerForm(f => ({ ...f, guest_id_type: v }))}>
                  <SelectTrigger data-testid="buyer-id-type-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NRIC">NRIC (Malaysian IC)</SelectItem>
                    <SelectItem value="PASSPORT">Passport</SelectItem>
                    <SelectItem value="BRN">BRN (Business Reg.)</SelectItem>
                    <SelectItem value="ARMY">Army ID</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>ID Number *</Label>
                <Input
                  value={buyerForm.guest_id_value}
                  onChange={e => setBuyerForm(f => ({ ...f, guest_id_value: e.target.value }))}
                  placeholder="ID number"
                  data-testid="buyer-id-value-input"
                />
              </div>
              <div>
                <Label>SST Registration No.</Label>
                <Input
                  value={buyerForm.buyer_sst}
                  onChange={e => setBuyerForm(f => ({ ...f, buyer_sst: e.target.value }))}
                  placeholder="NA"
                  data-testid="buyer-sst-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  value={buyerForm.buyer_email}
                  onChange={e => setBuyerForm(f => ({ ...f, buyer_email: e.target.value }))}
                  placeholder="email@example.com"
                  data-testid="buyer-email-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={buyerForm.buyer_phone}
                  onChange={e => setBuyerForm(f => ({ ...f, buyer_phone: e.target.value }))}
                  placeholder="Phone number"
                  data-testid="buyer-phone-input"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={buyerForm.buyer_address}
                onChange={e => setBuyerForm(f => ({ ...f, buyer_address: e.target.value }))}
                placeholder="Street address"
                data-testid="buyer-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>City</Label>
                <Input
                  value={buyerForm.buyer_city}
                  onChange={e => setBuyerForm(f => ({ ...f, buyer_city: e.target.value }))}
                  placeholder="City"
                  data-testid="buyer-city-input"
                />
              </div>
              <div>
                <Label>State</Label>
                <Select value={buyerForm.buyer_state} onValueChange={v => setBuyerForm(f => ({ ...f, buyer_state: v }))}>
                  <SelectTrigger data-testid="buyer-state-select"><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Johor">Johor</SelectItem>
                    <SelectItem value="Kedah">Kedah</SelectItem>
                    <SelectItem value="Kelantan">Kelantan</SelectItem>
                    <SelectItem value="Melaka">Melaka</SelectItem>
                    <SelectItem value="Negeri Sembilan">Negeri Sembilan</SelectItem>
                    <SelectItem value="Pahang">Pahang</SelectItem>
                    <SelectItem value="Perak">Perak</SelectItem>
                    <SelectItem value="Perlis">Perlis</SelectItem>
                    <SelectItem value="Pulau Pinang">Pulau Pinang</SelectItem>
                    <SelectItem value="Sabah">Sabah</SelectItem>
                    <SelectItem value="Sarawak">Sarawak</SelectItem>
                    <SelectItem value="Selangor">Selangor</SelectItem>
                    <SelectItem value="Terengganu">Terengganu</SelectItem>
                    <SelectItem value="W.P. Kuala Lumpur">W.P. Kuala Lumpur</SelectItem>
                    <SelectItem value="W.P. Labuan">W.P. Labuan</SelectItem>
                    <SelectItem value="W.P. Putrajaya">W.P. Putrajaya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Postal Code</Label>
                <Input
                  value={buyerForm.buyer_postal_code}
                  onChange={e => setBuyerForm(f => ({ ...f, buyer_postal_code: e.target.value }))}
                  placeholder="Postal code"
                  data-testid="buyer-postal-input"
                />
              </div>
              <div>
                <Label>Country Code</Label>
                <Select value={buyerForm.buyer_country} onValueChange={v => setBuyerForm(f => ({ ...f, buyer_country: v }))}>
                  <SelectTrigger data-testid="buyer-country-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MYS">MYS - Malaysia</SelectItem>
                    <SelectItem value="SGP">SGP - Singapore</SelectItem>
                    <SelectItem value="IDN">IDN - Indonesia</SelectItem>
                    <SelectItem value="THA">THA - Thailand</SelectItem>
                    <SelectItem value="PHL">PHL - Philippines</SelectItem>
                    <SelectItem value="VNM">VNM - Vietnam</SelectItem>
                    <SelectItem value="BRN">BRN - Brunei</SelectItem>
                    <SelectItem value="CHN">CHN - China</SelectItem>
                    <SelectItem value="JPN">JPN - Japan</SelectItem>
                    <SelectItem value="KOR">KOR - South Korea</SelectItem>
                    <SelectItem value="IND">IND - India</SelectItem>
                    <SelectItem value="AUS">AUS - Australia</SelectItem>
                    <SelectItem value="GBR">GBR - United Kingdom</SelectItem>
                    <SelectItem value="USA">USA - United States</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBuyerModal(false)}>Cancel</Button>
            <Button onClick={handleSaveBuyer} disabled={buyerSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-buyer-btn">
              {buyerSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <UserPen className="w-4 h-4 mr-2" />}
              Save Buyer Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Verification Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="max-w-sm" data-testid="qr-code-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-600" /> e-Invoice Verification
            </DialogTitle>
            <DialogDescription>Scan to verify on LHDN MyInvois portal</DialogDescription>
          </DialogHeader>
          {qrInvoice && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl shadow-md border" data-testid="qr-code-image">
                  <QRCodeSVG value={qrInvoice.qr_code_url || ''} size={200} level="H" includeMargin />
                </div>
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium text-slate-700 dark:text-gray-200">{qrInvoice.invoice_number}</p>
                <p className="text-xs text-slate-500 dark:text-gray-400">{qrInvoice.guest_name}</p>
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" /> VALIDATED
                </Badge>
              </div>
              <div className="text-center">
                <p className="text-[11px] text-slate-400 dark:text-gray-500 mb-1">UUID</p>
                <p className="font-mono text-xs text-slate-600 dark:text-gray-300 break-all" data-testid="qr-uuid">{qrInvoice.lhdn_uuid}</p>
              </div>
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => window.open(qrInvoice.qr_code_url, '_blank')}
                  data-testid="qr-verify-link"
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> Open in MyInvois
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Link Modal */}
      <Dialog open={showPaymentLinkModal} onOpenChange={setShowPaymentLinkModal}>
        <DialogContent className="max-w-md" data-testid="payment-link-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Guest Payment Link
            </DialogTitle>
            <DialogDescription>Share this link with the guest to collect payment</DialogDescription>
          </DialogHeader>
          {paymentLinkData && (
            <div className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-gray-400">Invoice</span>
                  <span className="font-mono font-medium">{paymentLinkData.invoiceNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-gray-400">Amount</span>
                  <span className="font-bold text-blue-600">
                    {new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(paymentLinkData.amount || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-gray-400">Expires</span>
                  <span className="text-slate-600 dark:text-gray-300">
                    {new Date(paymentLinkData.expiresAt).toLocaleDateString('en-MY', { 
                      day: 'numeric', month: 'short', year: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-slate-500 dark:text-gray-400">Payment Link</Label>
                <div className="flex gap-2">
                  <Input 
                    value={paymentLinkData.url} 
                    readOnly 
                    className="text-xs font-mono bg-slate-100 dark:bg-slate-900"
                    data-testid="payment-link-input"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={copyPaymentLink}
                    data-testid="copy-payment-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={copyPaymentLink}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy Link
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => window.open(paymentLinkData.url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open
                </Button>
              </div>

              <p className="text-xs text-slate-500 dark:text-gray-400 text-center">
                The guest will be redirected to Stripe to complete payment securely.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjustment (CN/DN) — now uses dedicated pages at /einvoice/adjustment */}
    </div>
  );
};

export default EInvoiceList;
